import type { ReactNode } from 'react';
import { makeStyles, tokens, Spinner, MessageBar, MessageBarBody } from '@fluentui/react-components';
import { DataTable, type DataTableColumn } from './DataTable';
import { EmptyState } from './EmptyState';

/**
 * The shape every list hook returns. Hooks start out as stubs returning an
 * empty list; when the API endpoint exists, only the hook changes.
 */
export interface ListState<T> {
  items: T[];
  loading: boolean;
  error: string | null;
}

const useStyles = makeStyles({
  centred: {
    display: 'flex',
    justifyContent: 'center',
    padding: tokens.spacingVerticalXXXL,
  },
});

interface AsyncListProps<T> {
  state: ListState<T>;
  columns: DataTableColumn<T>[];
  keyExtractor: (item: T) => string;
  loadingLabel: string;
  empty: {
    icon: ReactNode;
    title: string;
    description: string;
  };
}

/**
 * Renders a list hook's state: spinner while loading, an error bar on
 * failure, the empty state when there is nothing to show, otherwise a table.
 * Never renders sample rows.
 */
export function AsyncList<T>({ state, columns, keyExtractor, loadingLabel, empty }: AsyncListProps<T>) {
  const styles = useStyles();

  if (state.loading) {
    return (
      <div className={styles.centred}>
        <Spinner label={loadingLabel} />
      </div>
    );
  }

  if (state.error) {
    return (
      <MessageBar intent="error">
        <MessageBarBody>{state.error}</MessageBarBody>
      </MessageBar>
    );
  }

  if (state.items.length === 0) {
    return <EmptyState icon={empty.icon} title={empty.title} description={empty.description} />;
  }

  return <DataTable columns={columns} data={state.items} keyExtractor={keyExtractor} />;
}
